import { useCallback, useState } from "react";

/**
 * A piece of state that survives a reload, kept in this browser.
 *
 * For how somebody likes a screen arranged — which way a list is grouped,
 * which columns are shown, what has been cleared from an inbox. Storage can
 * be blocked or full; then it is ordinary state and is forgotten on reload,
 * which is the right way for a preference to fail.
 */
export function useStored<T>(key: string, fallback: T): [T, (next: T | ((current: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next: T | ((current: T) => T)) => {
      setValue((current) => {
        const resolved = typeof next === "function" ? (next as (current: T) => T)(current) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Nothing to do: the preference lasts until the page is closed.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}
